import {
  RecordClass,
  RecordInstance,
  TableField,
  TableValue,
} from '@veupathdb/wdk-client/lib/Utils/WdkModel';
import * as t from 'io-ts';
import { useState } from 'react';
import { EdaScatterPlot } from './eda/EdaScatterPlot';
import { EdaBarPlot } from './eda/EdaBarPlot';
import { EdaBoxPlot } from './eda/EdaBoxPlot';
import { Link } from 'react-router-dom';
import { CollapsibleSection } from '@veupathdb/wdk-client/lib/Components';

const PlotConfig = t.type({
  plotName: t.string,
  plotType: t.string,
  xAxisEntityId: t.string,
  xAxisVariableId: t.string,
  yAxisEntityId: t.string,
  yAxisVariableId: t.string,
  displaySpecVariableId: t.string,
  displayMode: t.union([t.literal('highlight'), t.literal('subset')]),
});

/**
 * Plot component per `plotType`. A type with no entry here renders an explicit
 * message rather than falling back to some other plot, so an unhandled type is
 * visible instead of being silently drawn as the wrong chart.
 */
const PLOT_COMPONENTS = {
  bar: EdaBarPlot,
  boxplot: EdaBoxPlot,
  scatterplot: EdaScatterPlot,
} as const;

const PlotConfigs = t.array(PlotConfig);

type RowData = TableValue[number];

interface RecordTableProps {
  record: RecordInstance;
  recordClass: RecordClass;
  value: TableValue;
  table: TableField;
  className?: string;
  searchTerm?: string;
  onSearchTermChange?: (searchTerm: string) => void;
}

interface DataTable {
  value: TableValue;
  table: TableField;
  record: RecordInstance;
  recordClass: RecordClass;
  DefaultComponent: React.ComponentType<RecordTableProps>;
}

interface Props {
  rowIndex: number;
  rowData: RowData;
  dataTable: DataTable;
}

export function EdaDatasetGraph(props: Props) {
  const {
    rowData: {
      plot_configs_json,
      dataset_id,
      dataset_name,
      graph_ids,
      source_id,
      default_graph_id,
      has_graph_data,
    },
    dataTable,
  } = props;

  const [selectedPlotsIndex, setSelectedPlotsIndex] = useState([0]);
  const [dataTableCollapsed, setDataTableCollapsed] = useState(false);

  // simple type guarding against `AttributeValue` fields not being `string`.
  // NOTE: `plot_configs_json` is intentionally NOT required here — a dataset with
  // no configured graph has a null plot config, and we still want to render its
  // data table below. Only the identity fields are required.
  if (
    typeof dataset_id !== 'string' ||
    typeof source_id !== 'string' ||
    typeof graph_ids !== 'string'
  ) {
    console.error('EdaDatasetGraph: bad props');
    return null;
  }

  const plotConfigs =
    typeof plot_configs_json === 'string'
      ? parseJson(plot_configs_json)
      : undefined;
  const showGraph =
    plotConfigs != null && plotConfigs.length > 0 && has_graph_data;

  const graphIds = graph_ids.split(/\s*,\s*/);

  const selectedPlotConfigs =
    plotConfigs != null
      ? plotConfigs.filter((_, index) => selectedPlotsIndex.includes(index))
      : [];

  return (
    <div>
      {showGraph && plotConfigs && (
        <>
          <h4>Choose graph(s) to display</h4>
          {plotConfigs.map((plotConfig, index) => {
            return (
              <label key={plotConfig.plotName}>
                <input
                  type="checkbox"
                  checked={selectedPlotsIndex.includes(index)}
                  onChange={(e) => {
                    setSelectedPlotsIndex((current) => {
                      return e.target.checked
                        ? current.concat(index).sort()
                        : current.filter((i) => i !== index);
                    });
                  }}
                />{' '}
                {plotConfig.plotName}{' '}
              </label>
            );
          })}

          {default_graph_id !== source_id ? (
            <div>
              <strong style={{ color: 'firebrick' }}>WARNING</strong>: This Gene
              ({source_id}) does not have data for this experiment. Instead, we
              are showing data for this same gene(s) from the reference strain
              for this species. This may or may NOT accurately represent the
              gene you are interested in.{' '}
            </div>
          ) : null}

          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
            }}
          >
            {selectedPlotConfigs.map((plotConfig) => {
              const xAxisVariable = {
                entityId: plotConfig.xAxisEntityId,
                variableId: plotConfig.xAxisVariableId,
              };
              const yAxisVariable = {
                entityId: plotConfig.yAxisEntityId,
                variableId: plotConfig.yAxisVariableId,
              };
              const geneDisplaySpec = graphIds && {
                ids: graphIds,
                variableId: plotConfig.displaySpecVariableId,
                // A starting point only: the adapters correct this to whichever
                // entity actually declares the variable, which is the x-axis
                // entity only when both axes share one.
                entityId: plotConfig.xAxisEntityId,
                traceName: source_id,
                mode: plotConfig.displayMode,
              };

              const PlotComponent =
                PLOT_COMPONENTS[
                  plotConfig.plotType as keyof typeof PLOT_COMPONENTS
                ];

              if (PlotComponent == null) {
                return (
                  <div key={plotConfig.plotName}>
                    {plotConfig.plotName}: unsupported plot type "
                    {plotConfig.plotType}"
                  </div>
                );
              }

              return (
                <div
                  key={plotConfig.plotName}
                  style={{ width: plotConfig.plotType === 'bar' ? 800 : 500 }}
                >
                  <PlotComponent
                    datasetId={dataset_id}
                    xAxisVariable={xAxisVariable}
                    yAxisVariable={yAxisVariable}
                    geneDisplaySpec={geneDisplaySpec}
                    plotTitle={plotConfig.plotName}
                  />
                </div>
              );
            })}
          </div>
        </>
      )}
      <div>
        <div style={{ display: 'flex', gap: '3ex' }}>
          {/* <h4>
            <Link
              to={`/workspace/analyses/${dataset_id}/new/visualizations/new`}
            >
              Use the Study Explorer for more advanced plot options
            </Link>
          </h4>
         */}
          <h4>
            <Link to={`/record/dataset/${dataset_id}`}>
              See the full dataset description
            </Link>
          </h4>
        </div>

        {props.dataTable && (
          <CollapsibleSection
            className={'eupathdb-' + props.dataTable.table.name + 'Container'}
            headerContent="Data table"
            headerComponent="h4"
            isCollapsed={dataTableCollapsed}
            onCollapsedChange={setDataTableCollapsed}
          >
            <dataTable.DefaultComponent
              record={dataTable.record}
              recordClass={dataTable.recordClass}
              table={dataTable.table}
              value={dataTable.value.filter(
                (dat) => dat.dataset_id === dataset_name
              )}
            />
          </CollapsibleSection>
        )}
      </div>
    </div>
  );
}

function parseJson(json: string) {
  try {
    const object = JSON.parse(json);
    if (PlotConfigs.is(object)) {
      return object;
    }
    return undefined;
  } catch (error) {
    console.error(error);
    return undefined;
  }
}
